import React, { Dispatch, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, Title } from '@xsolla/ui-core';
import { AlertExclamationColorful } from '@xsolla/ui-icons';
import { Modal } from '@xsolla/ui-modal';

import { setUserEmail } from '../../../../api';
import { FormFooter } from '../../../ui';
import { Action, ActionType } from '../types';
import { DeleteClientSubtitle, SuccessModalWrapper } from './style';

type Props = {
  emailTaken: string;
  email: string;
  userId: string;
  projectId: string;
  fetching: boolean;
  dispatch: Dispatch<Action>;
  onClose: () => void;
};

export const ForceUpdateModal = ({
  onClose,
  email,
  emailTaken,
  userId,
  fetching,
  dispatch,
  projectId
}: Props): JSX.Element => {
  const intl = useIntl();
  const [agreedWarning, setAgreedWarning] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const setEmailHandle = async () => {
    if (!emailTaken) return;

    dispatch({ type: ActionType.changeEmailStart });
    try {
      await setUserEmail(projectId, userId, {
        email,
        email_confirmation_needed: false,
        force_unlink: true
      });
      onClose();
    } catch (err) {
      setError(true);
    } finally {
      dispatch({ type: ActionType.changeEmailEnd });
    }
  };

  return (
    <Modal>
      <SuccessModalWrapper>
        <AlertExclamationColorful size={40} />
        <Title center indent="sm" data-test-id="update-email-modal-header">
          {intl.formatMessage({ id: 'users.settings.update-email.modal.title' })}
        </Title>
        <DeleteClientSubtitle>
          {intl.formatMessage(
            { id: 'users.settings.update-email.modal.subtitle' },
            { id: emailTaken }
          )}
        </DeleteClientSubtitle>
        <Checkbox onChange={(e) => setAgreedWarning(e.target.checked)}>
          <b>{intl.messages['users.settings.update-email.modal.checkbox']}</b>
        </Checkbox>
      </SuccessModalWrapper>

      <FormFooter
        isModal
        onSubmit={setEmailHandle}
        onCancel={onClose}
        fetching={fetching}
        error={error}
        disabled={!agreedWarning}
        submitText={intl.formatMessage({ id: 'users.settings.update-email.modal.submit' })}
        submitButtonPalette="action"
        data-test-id="update-email-modal-footer"
      />
    </Modal>
  );
};
